import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, CheckCircle, AlertTriangle, BookOpen, Heart, Shield, Users, Baby, Eye, Brain, Target, Play, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'tp-iepe-bloc1-progress';

const TPIEPEBloc1Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showResume, setShowResume] = useState(false);
  const [savedSlide, setSavedSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);

  // Restore progress on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const idx = parseInt(saved, 10);
        if (idx > 0 && idx < 41) {
          setSavedSlide(idx);
          setShowResume(true);
        }
      }
    } catch {}
  }, []);

  // Save progress on slide change
  const updateSlide = useCallback((idx) => {
    setCurrentSlide(idx);
    try { localStorage.setItem(STORAGE_KEY, String(idx)); } catch {}
  }, []);

  const THEME = {
    primary: '#059669',
    secondary: '#10B981',
    gradient: 'linear-gradient(135deg, #059669 0%, #10B981 50%, #34D399 100%)',
    sectionGradient: 'linear-gradient(135deg, #065F46 0%, #059669 100%)',
    light: '#ECFDF5',
    accent: '#047857'
  };

  const slides = [
    // SLIDE 1 — Titre
    {
      type: 'title',
      title: "Bloc 1",
      subtitle: "Contribuer à l'accueil des jeunes enfants et à la réponse à leurs besoins fondamentaux",
      narration: "Bienvenue dans le Bloc 1 du Titre Professionnel Intervenant Éducatif Petite Enfance. Ce bloc porte sur l'accueil des jeunes enfants et la réponse à leurs besoins fondamentaux. Le référentiel précise que le professionnel s'appuie sur l'analyse de ses observations pour adapter ses réponses aux besoins du jeune enfant."
    },
    // SLIDE 2 — Sommaire
    {
      type: 'sommaire',
      title: "Sommaire du Bloc 1",
      sections: [
        { num: "Intro", title: "Introduction générale", items: ["Méthode attendue", "Modèle de Virginia Henderson", "Les 14 besoins fondamentaux"], color: THEME.primary, targetSlide: 2 },
        { num: "1", title: "Observer les besoins de l'enfant", items: ["Observation professionnelle", "Singularité de l'enfant", "Identifier les besoins selon Henderson"], color: "#E75480", targetSlide: 10 },
        { num: "2", title: "Répondre aux besoins physiologiques", items: ["Boire et manger", "Éliminer", "Se mouvoir", "Dormir", "Température, vêtir, hygiène, respirer"], color: "#3B82F6", targetSlide: 17 },
        { num: "3", title: "Répondre aux besoins affectifs et relationnels", items: ["Sécurité affective", "Communication", "Apprendre et jouer", "Respect des valeurs"], color: "#F97316", targetSlide: 25 },
        { num: "4", title: "Participer au projet éducatif", items: ["Le projet éducatif", "Rôle du professionnel", "Démarche Observation-Analyse-Action-Évaluation"], color: "#8B5CF6", targetSlide: 33 }
      ],
      narration: "Voici le sommaire du Bloc 1. Nous commencerons par l'introduction générale avec le modèle de Virginia Henderson. Puis le chapitre 1 sur l'observation des besoins. Le chapitre 2 sur les besoins physiologiques. Le chapitre 3 sur les besoins affectifs et relationnels. Et enfin le chapitre 4 sur la participation au projet éducatif."
    },
    // SLIDE 3 — Introduction
    {
      type: 'section',
      title: "INTRODUCTION GÉNÉRALE",
      subtitle: "Comprendre les fondements de l'accompagnement du jeune enfant",
      chapterIcon: "book",
      chapterNum: "Intro",
      chapterColor: THEME.primary,
      narration: "Commençons par l'introduction générale. L'accompagnement du jeune enfant repose sur une compréhension fine de ses besoins. Le professionnel ne se limite pas à faire un soin ou surveiller. Il observe, analyse, puis ajuste sa posture et ses actions."
    },
    // SLIDE 4 — Méthode attendue
    {
      type: 'content',
      title: "Ce que tu dois montrer dans le Bloc 1",
      points: [
        "Repérer un besoin",
        "Le relier à une situation réelle",
        "Justifier ton action",
        "Expliquer en quoi ta réponse est adaptée à cet enfant-là, à ce moment-là"
      ],
      highlight: "Le Bloc 1 repose sur le modèle de Virginia Henderson et sur la logique professionnelle suivante : Observation, Analyse, Action, Évaluation.",
      narration: "Dans le bloc 1, il s'agit de montrer que tu sais repérer un besoin, le relier à une situation réelle, justifier ton action, et expliquer en quoi ta réponse est adaptée à cet enfant-là, à ce moment-là. La méthode repose sur quatre étapes : observation, analyse, action, évaluation."
    },
    // SLIDE 5 — Méthode OAAE
    {
      type: 'method',
      title: "Méthode attendue par le jury",
      steps: [
        { label: "Observation", desc: "Je recueille des faits, des signes, des comportements", icon: "eye" },
        { label: "Analyse", desc: "J'identifie le besoin en jeu et je fais des hypothèses", icon: "brain" },
        { label: "Action", desc: "J'adapte ma réponse à cet enfant, à ce moment", icon: "hand" },
        { label: "Évaluation", desc: "Je vérifie l'effet de mon action et j'ajuste si besoin", icon: "check" }
      ],
      quote: "« J'observe que… cela me permet d'identifier le besoin de… j'adapte donc… puis j'évalue… »",
      narration: "La méthode attendue par le jury comporte quatre étapes. Premièrement, l'observation : je recueille des faits, des signes, des comportements. Deuxièmement, l'analyse : j'identifie le besoin en jeu. Troisièmement, l'action : j'adapte ma réponse. Quatrièmement, l'évaluation : je vérifie l'effet de mon action. Tu dois être capable de formuler : j'observe que, cela me permet d'identifier le besoin de, j'adapte donc, puis j'évalue."
    },
    // SLIDE 6 — Référentiel qualité
    {
      type: 'content',
      title: "Référentiel national qualité d'accueil",
      points: [
        "Posture fondée sur l'empathie",
        "Non-jugement",
        "Verbalisation systématique",
        "Sécurité affective de l'enfant",
        "Respect de l'autonomie de l'enfant"
      ],
      highlight: "Le référentiel national de la qualité d'accueil insiste sur une posture fondée sur l'empathie, le non-jugement, la verbalisation, la sécurité affective et le respect de l'autonomie de l'enfant.",
      narration: "Le référentiel national de la qualité d'accueil insiste sur une posture fondée sur l'empathie, le non-jugement, la verbalisation systématique, la sécurité affective et le respect de l'autonomie de l'enfant. Ce sont les fondements de ta pratique professionnelle."
    },
    // SLIDE 7 — Virginia Henderson
    {
      type: 'section',
      title: "VIRGINIA HENDERSON",
      subtitle: "Les 14 besoins fondamentaux",
      chapterIcon: "heart",
      chapterNum: "",
      chapterColor: "#E75480",
      narration: "Découvrons maintenant Virginia Henderson et son modèle des 14 besoins fondamentaux, un outil essentiel pour structurer ta pratique professionnelle."
    },
    // SLIDE 8 — Fiche Henderson
    {
      type: 'content',
      title: "Qui est Virginia Henderson ?",
      points: [
        "Infirmière et théoricienne des soins",
        "A développé une approche centrée sur l'aide à la personne",
        "Objectif : retrouver ou conserver le plus d'autonomie possible",
        "Connue pour avoir formalisé les 14 besoins fondamentaux"
      ],
      highlight: "Même si ce modèle vient du soin infirmier, il est très utile en IEPE car il permet de structurer l'observation, comprendre les besoins, relier une situation concrète à un besoin fondamental, et justifier une action professionnelle.",
      narration: "Virginia Henderson est une infirmière et théoricienne des soins. Elle a développé une approche centrée sur l'aide apportée à la personne afin qu'elle retrouve ou conserve le plus d'autonomie possible. Même si ce modèle vient du soin infirmier, il est très utile en IEPE car il permet de structurer l'observation, comprendre les besoins du jeune enfant, relier une situation concrète à un besoin fondamental, et justifier une action professionnelle."
    },
    // SLIDE 9 — Les 14 besoins
    {
      type: 'grid',
      title: "Les 14 besoins fondamentaux de Virginia Henderson",
      items: [
        { num: "1", label: "Respirer" },
        { num: "2", label: "Boire et manger" },
        { num: "3", label: "Éliminer" },
        { num: "4", label: "Se mouvoir et maintenir une bonne posture" },
        { num: "5", label: "Dormir et se reposer" },
        { num: "6", label: "Se vêtir et se dévêtir" },
        { num: "7", label: "Maintenir sa température corporelle" },
        { num: "8", label: "Être propre, soigné, protéger ses téguments" },
        { num: "9", label: "Éviter les dangers" },
        { num: "10", label: "Communiquer" },
        { num: "11", label: "Agir selon ses croyances et ses valeurs" },
        { num: "12", label: "S'occuper en vue de se réaliser" },
        { num: "13", label: "Se recréer" },
        { num: "14", label: "Apprendre" }
      ],
      narration: "Les 14 besoins fondamentaux de Virginia Henderson sont : 1 Respirer, 2 Boire et manger, 3 Éliminer, 4 Se mouvoir et maintenir une bonne posture, 5 Dormir et se reposer, 6 Se vêtir et se dévêtir, 7 Maintenir sa température corporelle, 8 Être propre et soigné, 9 Éviter les dangers, 10 Communiquer, 11 Agir selon ses croyances et valeurs, 12 S'occuper en vue de se réaliser, 13 Se recréer, 14 Apprendre."
    },
    // SLIDE 10 — Idée centrale Henderson
    {
      type: 'content',
      title: "Idée centrale de Virginia Henderson",
      points: [
        "Le professionnel accompagne l'enfant pour répondre à ses besoins fondamentaux",
        "Sans faire systématiquement à sa place",
        "En favorisant progressivement son autonomie"
      ],
      quote: "« Je m'appuie sur le modèle de Virginia Henderson pour repérer les besoins fondamentaux de l'enfant. Cela m'aide à analyser une situation de façon professionnelle et à adapter ma réponse. »",
      narration: "L'idée centrale du modèle de Henderson est que le professionnel accompagne l'enfant pour répondre à ses besoins fondamentaux, sans faire systématiquement à sa place, en favorisant progressivement son autonomie. Tu peux dire au jury : je m'appuie sur le modèle de Virginia Henderson pour repérer les besoins fondamentaux de l'enfant, cela m'aide à analyser une situation de façon professionnelle et à adapter ma réponse."
    },
    // SLIDE 11 — CHAPITRE 1
    {
      type: 'section',
      title: "CHAPITRE 1",
      subtitle: "Observer les besoins de l'enfant en tenant compte de sa singularité",
      chapterIcon: "eye",
      chapterNum: "01",
      chapterColor: "#E75480",
      narration: "Chapitre 1 : Observer les besoins de l'enfant en tenant compte de sa singularité. L'objectif est de développer une observation professionnelle permettant d'identifier les besoins fondamentaux de chaque enfant."
    },
    // SLIDE 12 — Observation professionnelle
    {
      type: 'content',
      title: "L'observation professionnelle",
      points: [
        "Observer, ce n'est pas seulement regarder",
        "Recueillir des faits objectifs",
        "Repérer des signes concrets",
        "Décrire une situation sans jugement",
        "Rester dans une posture neutre",
        "Analyser ensuite, pas pendant"
      ],
      highlight: "L'observation se fait au quotidien : pendant l'accueil, lors du jeu, pendant les repas, au moment du change, au coucher, dans les interactions avec les autres.",
      narration: "Observer, ce n'est pas seulement regarder. C'est recueillir des faits objectifs, repérer des signes concrets, décrire une situation sans jugement, rester dans une posture neutre, et analyser ensuite. L'observation se fait au quotidien : pendant l'accueil, lors du jeu, pendant les repas, au moment du change, au coucher, dans les interactions avec les autres."
    },
    // SLIDE 13 — Observation vs Interprétation
    {
      type: 'comparison',
      title: "Observation vs Interprétation",
      wrong: [
        { label: "Il est capricieux", correct: "Il pleure quand l'adulte s'éloigne" },
        { label: "Il est agressif", correct: "Il pousse un autre enfant quand celui-ci s'approche du jouet" },
        { label: "Il est paresseux", correct: "Il ne se déplace pas vers l'activité proposée" },
        { label: "Il n'écoute pas", correct: "Il détourne le regard et quitte l'espace au moment de la consigne" }
      ],
      highlight: "Une bonne observation décrit ce que je vois, ce que j'entends, ce que l'enfant fait, dans quel contexte.",
      narration: "Attention à bien distinguer l'interprétation de l'observation professionnelle. Ne dis pas il est capricieux, mais il pleure quand l'adulte s'éloigne. Ne dis pas il est agressif, mais il pousse un autre enfant quand celui-ci s'approche du jouet. Ne dis pas il est paresseux, mais il ne se déplace pas vers l'activité proposée. Une bonne observation décrit ce que tu vois, ce que tu entends, ce que l'enfant fait, dans quel contexte."
    },
    // SLIDE 14 — Singularité de l'enfant
    {
      type: 'content',
      title: "La singularité de l'enfant",
      points: [
        "Son rythme biologique",
        "Son développement moteur et affectif",
        "Son histoire familiale",
        "Ses habitudes",
        "Son tempérament",
        "Son état de santé",
        "Son vécu du moment"
      ],
      highlight: "Un même besoin n'appelle pas toujours la même réponse. Deux enfants peuvent pleurer à la séparation : pour l'un, un doudou suffit ; pour l'autre, il faudra un temps de bras et une transition plus lente.",
      narration: "Chaque enfant est unique. Un même âge ne veut pas dire un même rythme. La singularité prend en compte son rythme biologique, son développement moteur et affectif, son histoire familiale, ses habitudes, son tempérament, son état de santé et son vécu du moment. Un même besoin n'appelle pas toujours la même réponse."
    },
    // SLIDE 15 — Identifier les besoins
    {
      type: 'table',
      title: "Identifier les besoins fondamentaux — Tableau repère",
      rows: [
        { situation: "L'enfant frotte ses yeux, bâille", besoin: "Dormir / se reposer" },
        { situation: "Il tourne la tête au repas", besoin: "Boire et manger / respect du rythme" },
        { situation: "Il pleure quand l'adulte part", besoin: "Communiquer / sécurité affective" },
        { situation: "Il jette les objets", besoin: "Se mouvoir / jouer / explorer" },
        { situation: "Il reste figé dans un coin", besoin: "Sécurité / relation / adaptation" },
        { situation: "Il tire sur sa couche", besoin: "Être propre / éliminer / inconfort" },
        { situation: "Il tend les bras vers l'adulte", besoin: "Relation / communication / sécurité" }
      ],
      narration: "Voici un tableau repère pour identifier les besoins. Quand un enfant frotte ses yeux et bâille, c'est le besoin de dormir. Quand il tourne la tête au repas, c'est le respect du rythme alimentaire. Quand il pleure au départ de l'adulte, c'est le besoin de sécurité affective. Quand il jette les objets, c'est le besoin de se mouvoir et d'explorer. Quand il reste figé, c'est le besoin de sécurité et de relation."
    },
    // SLIDE 16 — Exemple d'analyse
    {
      type: 'example',
      title: "Exemple d'analyse attendu au jury",
      situation: "Un enfant jette les jouets au sol de manière répétée.",
      observation: ["Il prend les objets", "Les lance", "Recommence plusieurs fois", "Regarde la chute", "Observe la réaction de l'adulte"],
      analyse: ["Besoin de manipulation", "Exploration sensorielle", "Besoin moteur", "Recherche de causalité", "Besoin d'attention selon le contexte"],
      action: ["Sécuriser l'espace", "Proposer du matériel adapté", "Verbaliser", "Accompagner sans étiqueter l'enfant"],
      formulation: "« J'observe que l'enfant lance les objets à plusieurs reprises. J'identifie un besoin d'exploration et de manipulation, ainsi qu'un besoin moteur. J'adapte donc ma réponse en proposant des objets adaptés à lancer ou à manipuler, tout en sécurisant l'environnement. »",
      narration: "Voici un exemple d'analyse. Situation : un enfant jette les jouets au sol de manière répétée. Observation : il prend les objets, les lance, recommence plusieurs fois, regarde la chute. Analyse : cela peut évoquer un besoin de manipulation, une exploration sensorielle, un besoin moteur. Action : sécuriser l'espace, proposer du matériel adapté, verbaliser. Formulation au jury : j'observe que l'enfant lance les objets à plusieurs reprises, j'identifie un besoin d'exploration et de manipulation, j'adapte donc ma réponse en proposant des objets adaptés."
    },
    // SLIDE 17 — Attendu jury ch1
    {
      type: 'jurybox',
      title: "Attendu du jury — Chapitre 1",
      points: [
        "Tu observes avant d'agir",
        "Tu ne colles pas d'étiquette à l'enfant",
        "Tu relies les comportements à des besoins",
        "Tu individualises ta réponse"
      ],
      narration: "Le jury attend que tu montres que tu observes avant d'agir, que tu ne colles pas d'étiquette à l'enfant, que tu relies les comportements à des besoins fondamentaux, et que tu individualises ta réponse."
    },
    // SLIDE 18 — CHAPITRE 2
    {
      type: 'section',
      title: "CHAPITRE 2",
      subtitle: "Répondre aux besoins physiologiques",
      chapterIcon: "shield",
      chapterNum: "02",
      chapterColor: "#3B82F6",
      narration: "Chapitre 2 : Répondre aux besoins physiologiques. L'objectif est d'assurer le confort, la santé, la sécurité et le bien-être de l'enfant dans les temps de vie quotidienne. Le référentiel qualité rappelle que les temps de repas, change et sommeil sont des temps éducatifs et relationnels, pas seulement des tâches techniques."
    },
    // SLIDE 19 — Boire et manger
    {
      type: 'content',
      title: "Boire et manger",
      points: [
        "Respecter le rythme de l'enfant",
        "Observer les signes de faim et de satiété",
        "Sécuriser la posture",
        "Favoriser le plaisir alimentaire",
        "Encourager l'autonomie",
        "Ne jamais forcer l'enfant à finir son assiette",
        "Laisser place à l'exploration sensorielle"
      ],
      quote: "« Tu tournes la tête, je vois que tu n'as peut-être plus faim. On va faire une pause. »",
      narration: "Le besoin alimentaire ne se résume pas à faire manger. Il faut respecter le rythme, observer les signes de faim et de satiété, sécuriser la posture, favoriser le plaisir et encourager l'autonomie. Le référentiel précise que les professionnels ne forcent pas l'enfant à finir son assiette et laissent place à l'exploration sensorielle."
    },
    // SLIDE 20 — Éliminer
    {
      type: 'content',
      title: "Éliminer : change et continence",
      points: [
        "Le change répond à un besoin d'hygiène, de confort, de santé et de relation",
        "C'est un moment de soin intime propice à un échange individuel",
        "Respecter l'hygiène et la sécurité",
        "Préserver l'intimité de l'enfant",
        "Verbaliser chaque geste",
        "Favoriser la participation de l'enfant si possible",
        "L'autonomie doit être favorisée selon l'âge et le développement"
      ],
      warning: "L'acquisition de la continence ne doit pas être forcée. On accompagne, on observe, on soutient.",
      narration: "Le change répond à un besoin d'hygiène, de confort, de santé et de relation. Le référentiel rappelle que le change est un moment de soin intime, propice à un échange individuel avec l'enfant. Il faut respecter l'hygiène, la sécurité, l'intimité, verbaliser chaque geste et favoriser la participation de l'enfant. Important : l'acquisition de la continence ne doit pas être forcée."
    },
    // SLIDE 21 — Se mouvoir
    {
      type: 'content',
      title: "Se mouvoir",
      points: [
        "Le besoin de mouvement est central chez le jeune enfant",
        "Il participe au développement moteur et à la confiance en soi",
        "Laisser de l'espace libre",
        "Éviter de faire à la place de l'enfant",
        "Proposer un environnement sécurisé",
        "Respecter la motricité libre"
      ],
      highlight: "Exemple : Un bébé tente de se retourner. Je sécurise le tapis, je l'encourage, je n'accélère pas le geste à sa place.",
      narration: "Le besoin de mouvement est central chez le jeune enfant. Il participe au développement moteur, à la confiance en soi et à l'exploration du monde. En pratique, il faut laisser de l'espace, éviter de faire à la place, proposer un environnement sécurisé et respecter la motricité libre. Par exemple, si un bébé tente de se retourner, je sécurise le tapis, je l'encourage, mais je n'accélère pas le geste à sa place."
    },
    // SLIDE 22 — Dormir
    {
      type: 'content',
      title: "Dormir et se reposer",
      points: [
        "Le sommeil est un besoin fondamental",
        "Respecter le rythme individuel de l'enfant",
        "Repérer les signes de fatigue",
        "Proposer un environnement calme",
        "Mettre en place des rituels sécurisants",
        "Assurer une surveillance adaptée"
      ],
      signes: ["Frottement des yeux", "Bâillements", "Agitation", "Pleurs", "Détournement du regard", "Besoin de succion", "Recherche des bras"],
      narration: "Le sommeil est un besoin fondamental. Le professionnel veille à respecter le rythme, repérer les signes de fatigue, proposer un environnement calme, mettre en place des rituels sécurisants et assurer une surveillance adaptée. Les signes de fatigue incluent le frottement des yeux, les bâillements, l'agitation, les pleurs, le détournement du regard, le besoin de succion et la recherche des bras."
    },
    // SLIDE 23 — Température, vêtir, propreté, respirer
    {
      type: 'multi',
      title: "Autres besoins physiologiques",
      sections: [
        { title: "Maintenir sa température", items: ["Adapter les vêtements", "Surveiller mains froides, nuque moite, rougeur", "Aérer la pièce"] },
        { title: "Se vêtir / se dévêtir", items: ["Favoriser l'autonomie même chez les petits", "Verbaliser : « Je vais passer ton bras… tu peux m'aider ? »"] },
        { title: "Être propre", items: ["Hygiène corporelle, lavage des mains", "Soin de la peau, prévention des infections"] },
        { title: "Respirer", items: ["Air sain, aération adaptée", "Vigilance sur l'encombrement nasal", "Environnement sans tabac"] }
      ],
      narration: "Les autres besoins physiologiques. Maintenir la température : adapter les vêtements, surveiller les signes d'inconfort. Se vêtir : favoriser l'autonomie en verbalisant les gestes. Être propre : hygiène corporelle et prévention des infections. Respirer : air sain, aération adaptée et environnement sans tabac."
    },
    // SLIDE 24 — Posture professionnelle
    {
      type: 'posture',
      title: "Posture professionnelle attendue",
      wrong: [
        "Faire à la place",
        "Aller trop vite",
        "Chercher l'efficacité avant la relation"
      ],
      right: [
        "Faire avec l'enfant",
        "Verbaliser chaque geste",
        "Respecter le rythme",
        "Sécuriser",
        "Observer avant, pendant et après"
      ],
      narration: "La posture professionnelle attendue : ne pas faire à la place, ne pas aller trop vite, ne pas chercher l'efficacité avant la relation. Il faut faire avec l'enfant, verbaliser, respecter le rythme, sécuriser, et observer avant, pendant et après chaque soin."
    },
    // SLIDE 25 — Exemple complet ch2
    {
      type: 'example',
      title: "Exemple complet — Refus de manger",
      situation: "Un enfant refuse de manger.",
      observation: ["Il tourne la tête", "Repousse la cuillère", "Pleure", "Se frotte les yeux"],
      analyse: ["Le refus n'est pas forcément de l'opposition", "Peut traduire une fatigue", "Une absence de faim", "Un inconfort", "Un besoin de pause"],
      action: ["Ne pas forcer", "Verbaliser", "Proposer plus tard si possible", "Transmettre à l'équipe"],
      evaluation: "L'enfant accepte peut-être mieux plus tard, il s'apaise, son rythme est respecté.",
      narration: "Exemple complet : un enfant refuse de manger. Observation : il tourne la tête, repousse la cuillère, pleure, se frotte les yeux. Analyse : le refus n'est pas forcément de l'opposition, il peut traduire une fatigue, une absence de faim ou un besoin de pause. Action : ne pas forcer, verbaliser, proposer plus tard. Évaluation : l'enfant accepte mieux plus tard, son rythme est respecté."
    },
    // SLIDE 26 — CHAPITRE 3
    {
      type: 'section',
      title: "CHAPITRE 3",
      subtitle: "Répondre aux besoins affectifs et relationnels",
      chapterIcon: "heart",
      chapterNum: "03",
      chapterColor: "#F97316",
      narration: "Chapitre 3 : Répondre aux besoins affectifs et relationnels. L'objectif est d'assurer la sécurité affective, le développement social et émotionnel de l'enfant. Le référentiel rappelle que le lien sécure repose sur la disponibilité sensible de l'adulte, son empathie et une relation individualisée."
    },
    // SLIDE 27 — Sécurité affective
    {
      type: 'content',
      title: "Être en sécurité — Les 3 piliers",
      points: [
        "La sécurité n'est pas seulement physique, elle est aussi affective",
        "L'enfant a besoin d'un cadre stable",
        "D'adultes repérables et prévisibles",
        "D'une parole rassurante",
        "De continuité dans l'accueil"
      ],
      pillars: ["Attachement", "Confiance", "Continuité"],
      narration: "La sécurité n'est pas seulement physique, elle est aussi affective. L'enfant a besoin d'un cadre stable, d'adultes repérables, d'une parole rassurante et de continuité. Les trois piliers de la sécurité affective sont l'attachement, la confiance et la continuité."
    },
    // SLIDE 28 — Éviter les dangers
    {
      type: 'content',
      title: "Éviter les dangers",
      points: [
        "Prévenir les risques physiques : chute, étouffement, matériel inadapté",
        "Prévenir les risques relationnels :"
      ],
      dangers: ["Cris", "Humiliations", "Menaces", "Séparation brutale", "Ignorance des émotions", "Non-respect du rythme"],
      narration: "Le professionnel prévient les risques physiques comme les chutes, l'étouffement et le matériel inadapté. Mais il doit aussi éviter les dangers relationnels : les cris, les humiliations, les menaces, la séparation brutale, l'ignorance des émotions et le non-respect du rythme de l'enfant."
    },
    // SLIDE 29 — Communiquer
    {
      type: 'content',
      title: "Communiquer",
      points: [
        "Le jeune enfant communique avant de parler : pleurs, mimiques, gestes, regard, tonus, sourires",
        "Le rôle du professionnel :",
        "Observer les signaux de l'enfant",
        "Nommer ce qu'il vit",
        "Répondre de manière adaptée",
        "Se mettre à hauteur de l'enfant",
        "Utiliser le prénom"
      ],
      highlight: "Le référentiel insiste sur une relation individualisée : parler à l'enfant, se déplacer vers lui, utiliser son prénom plutôt que des surnoms.",
      narration: "Le jeune enfant communique avant de parler : par les pleurs, les mimiques, les gestes, le regard, le tonus et les sourires. Le rôle du professionnel est d'observer ces signaux, de nommer ce que l'enfant vit, de répondre de manière adaptée, de se mettre à sa hauteur et d'utiliser son prénom. Le référentiel insiste sur une relation individualisée."
    },
    // SLIDE 30 — Apprendre et Jouer
    {
      type: 'content',
      title: "Apprendre et Jouer",
      points: [
        "Le jeune enfant apprend par le corps, l'exploration, la répétition, l'imitation et dans la relation",
        "Le professionnel stimule sans sursolliciter",
        "Le jeu n'est pas un temps pour passer le temps",
        "Jouer permet d'explorer, d'essayer, de comprendre, de se représenter et de créer du lien"
      ],
      highlight: "Le jeu est une activité essentielle du développement. L'enfant a aussi besoin de plaisir, de détente et de moments agréables.",
      narration: "Le jeune enfant apprend par le corps, l'exploration, la répétition, l'imitation et dans la relation. Le professionnel stimule sans sursolliciter. Le jeu n'est pas un temps pour passer le temps, c'est une activité essentielle du développement. Jouer permet d'explorer, d'essayer, de comprendre, de se représenter et de créer du lien."
    },
    // SLIDE 31 — Respect valeurs/famille
    {
      type: 'content',
      title: "Agir selon ses valeurs — Respect de la famille",
      points: [
        "Respecter la culture familiale",
        "Prendre en compte les habitudes",
        "Accepter certaines pratiques éducatives",
        "Respecter les rythmes de vie",
        "Dans le cadre du fonctionnement de la structure"
      ],
      posture: ["Accueillir sans juger", "Écouter", "Expliquer", "Chercher la cohérence possible avec la famille"],
      narration: "En petite enfance, respecter les valeurs implique de respecter la culture familiale, les habitudes et certaines pratiques éducatives, dans le cadre du fonctionnement de la structure. La posture professionnelle consiste à accueillir sans juger, écouter, expliquer et chercher la cohérence possible avec la famille."
    },
    // SLIDE 32 — Erreurs fréquentes
    {
      type: 'posture',
      title: "Erreurs fréquentes à éviter",
      wrong: [
        "Minimiser l'émotion",
        "Dire « ce n'est rien »",
        "Aller trop vite",
        "Prendre le pleur comme une opposition"
      ],
      right: [
        "Reconnaître l'émotion",
        "Verbaliser ce que l'enfant vit",
        "Contenir avec bienveillance",
        "Rassurer par la présence",
        "Proposer une transition"
      ],
      narration: "Attention aux erreurs fréquentes : ne pas minimiser l'émotion, ne pas dire ce n'est rien, ne pas aller trop vite, ne pas prendre le pleur comme une opposition. Il faut reconnaître l'émotion, verbaliser, contenir avec bienveillance, rassurer par la présence et proposer une transition."
    },
    // SLIDE 33 — Exemple séparation
    {
      type: 'example',
      title: "Exemple — La séparation",
      situation: "L'enfant pleure au départ de son parent.",
      observation: ["Pleurs intenses", "Regard vers la porte", "Corps tendu"],
      analyse: ["Émotion de séparation", "Besoin de sécurité affective", "Besoin de continuité"],
      action: ["Accueillir l'émotion", "Nommer ce qu'il vit", "Proposer un objet repère (doudou)", "Sécuriser par la voix et la présence", "Maintenir le cadre"],
      formulation: "« Tu es triste, papa vient de partir. C'est difficile. Je reste avec toi. On va aller regarder le coin lecture ensemble. »",
      narration: "Exemple : l'enfant pleure au départ de son parent. Observation : pleurs intenses, regard vers la porte, corps tendu. Analyse : émotion de séparation, besoin de sécurité affective. Action : accueillir l'émotion, nommer ce qu'il vit, proposer un objet repère comme un doudou, sécuriser par la voix et la présence. Tu peux verbaliser : tu es triste, papa vient de partir. C'est difficile. Je reste avec toi."
    },
    // SLIDE 34 — CHAPITRE 4
    {
      type: 'section',
      title: "CHAPITRE 4",
      subtitle: "Participer au projet éducatif",
      chapterIcon: "users",
      chapterNum: "04",
      chapterColor: "#8B5CF6",
      narration: "Chapitre 4 : Participer au projet éducatif. L'objectif est d'inscrire ta pratique dans une démarche collective, cohérente et réfléchie. Le référentiel précise que le professionnel participe à la mise en oeuvre, à l'évaluation et à l'ajustement du projet éducatif avec l'équipe."
    },
    // SLIDE 35 — Projet éducatif
    {
      type: 'content',
      title: "Le projet éducatif",
      points: [
        "C'est le cadre de référence de la structure",
        "Il exprime les valeurs partagées",
        "Les objectifs éducatifs",
        "Les pratiques choisies",
        "La façon d'accueillir les enfants et les familles"
      ],
      narration: "Le projet éducatif est le cadre de référence de la structure. Il exprime les valeurs partagées, les objectifs éducatifs, les pratiques choisies et la façon d'accueillir les enfants et les familles."
    },
    // SLIDE 36 — Lien besoins / projet
    {
      type: 'table',
      title: "Lien entre projet éducatif et besoins fondamentaux",
      rows: [
        { situation: "Respect du rythme", besoin: "Dormir, manger, sécurité" },
        { situation: "Autonomie", besoin: "Se mouvoir, se vêtir, manger" },
        { situation: "Relation bienveillante", besoin: "Communiquer, sécurité affective" },
        { situation: "Socialisation", besoin: "Communiquer, jouer" },
        { situation: "Développement global", besoin: "Apprendre, explorer, se recréer" }
      ],
      narration: "Le projet éducatif doit répondre à tous les besoins de l'enfant. Le respect du rythme répond aux besoins de dormir, manger et de sécurité. L'autonomie répond aux besoins de se mouvoir, se vêtir et manger. La relation bienveillante répond au besoin de communiquer et de sécurité affective. La socialisation au besoin de communiquer et jouer. Le développement global au besoin d'apprendre, explorer et se recréer."
    },
    // SLIDE 37 — Rôle du professionnel
    {
      type: 'content',
      title: "Rôle du professionnel dans le projet éducatif",
      points: [
        "Observer les enfants et les situations",
        "Transmettre ses observations à l'équipe",
        "Proposer des ajustements",
        "Ajuster sa pratique en fonction du projet",
        "Participer à la cohérence de l'équipe",
        "Évaluer les effets de ses actions"
      ],
      narration: "Le professionnel observe, transmet à l'équipe, propose des ajustements, ajuste sa pratique en fonction du projet, participe à la cohérence de l'équipe et évalue les effets de ses actions."
    },
    // SLIDE 38 — Démarche professionnelle
    {
      type: 'method',
      title: "La démarche professionnelle clé",
      steps: [
        { label: "Observation", desc: "Les enfants n'utilisent pas certains jeux", icon: "eye" },
        { label: "Analyse", desc: "Matériel peu adapté à leur stade de développement", icon: "brain" },
        { label: "Action", desc: "Proposer du matériel plus accessible et plus simple", icon: "hand" },
        { label: "Évaluation", desc: "Les enfants investissent davantage l'espace", icon: "check" }
      ],
      narration: "La démarche professionnelle en exemple concret. Observation : les enfants n'utilisent pas certains jeux. Analyse : le matériel est peu adapté à leur stade de développement. Action : proposer du matériel plus accessible et plus simple. Évaluation : les enfants investissent davantage l'espace."
    },
    // SLIDE 39 — SYNTHÈSE
    {
      type: 'section',
      title: "SYNTHÈSE GÉNÉRALE",
      subtitle: "Ce que le jury attend dans le Bloc 1",
      chapterIcon: "target",
      chapterNum: "",
      chapterColor: THEME.primary,
      narration: "Synthèse générale du Bloc 1. Voici ce que le jury attend de toi."
    },
    // SLIDE 40 — Synthèse points
    {
      type: 'jurybox',
      title: "Ce que le jury attend — Bloc 1",
      points: [
        "Tu identifies les besoins fondamentaux",
        "Tu fais le lien avec le modèle de Virginia Henderson",
        "Tu tiens compte de la singularité de l'enfant",
        "Tu adaptes ta pratique à chaque situation",
        "Tu analyses tes actions avec méthode",
        "Tu montres une posture professionnelle"
      ],
      quote: "« J'observe que… ce qui me permet d'identifier le besoin de… j'adapte donc… afin de… puis j'évalue… »",
      narration: "Le jury attend que tu identifies les besoins fondamentaux, que tu fasses le lien avec Henderson, que tu tiennes compte de la singularité de l'enfant, que tu adaptes ta pratique, que tu analyses tes actions et que tu montres une posture professionnelle. La phrase type à utiliser : j'observe que, ce qui me permet d'identifier le besoin de, j'adapte donc, afin de, puis j'évalue."
    },
    // SLIDE 41 — Félicitations
    {
      type: 'congratulations',
      title: "Bloc 1 terminé !",
      message: "Tu as parcouru l'ensemble du Bloc 1 du TP IEPE.",
      details: [
        "Introduction et modèle de Virginia Henderson",
        "Chapitre 1 : Observer les besoins",
        "Chapitre 2 : Répondre aux besoins physiologiques",
        "Chapitre 3 : Répondre aux besoins affectifs et relationnels",
        "Chapitre 4 : Participer au projet éducatif"
      ],
      narration: "Félicitations ! Tu as parcouru l'ensemble du Bloc 1. Tu connais maintenant le modèle de Virginia Henderson, l'observation professionnelle, les besoins physiologiques, affectifs et relationnels, et la participation au projet éducatif. Continue avec le quiz pour valider tes acquis !"
    }
  ];

  const handleTextToSpeech = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      return;
    }

    const slide = slides[currentSlide];
    let textToSpeak = slide.narration || '';

    if (!textToSpeak) {
      if (slide.type === 'title') textToSpeak = `${slide.title}. ${slide.subtitle}`;
      else if (slide.type === 'section') textToSpeak = `${slide.title}. ${slide.subtitle || ''}`;
      else textToSpeak = slide.title || '';
    }

    textToSpeak = textToSpeak
      .replace(/IEPE/g, 'I E P E')
      .replace(/TP/g, 'titre professionnel')
      .replace(/OAAE/g, 'observation analyse action évaluation');

    if (textToSpeak.length > 800) {
      textToSpeak = textToSpeak.substring(0, 800) + '...';
    }

    if (!textToSpeak.trim()) {
      toast.info("Pas de contenu audio pour cette slide");
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      const API_URL = window.location.origin;
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, speed: 1.1 })
      });

      if (!response.ok) throw new Error('TTS failed');
      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      setIsLoading(false);

      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => { toast.error("Erreur de lecture audio"); setIsPlaying(false); };
        audioRef.current.play().catch(() => { toast.error("Impossible de lire l'audio"); setIsPlaying(false); });
      } else {
        toast.error("Pas de données audio reçues");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error("Erreur lors de la génération audio");
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const nextSlide = () => { if (currentSlide < slides.length - 1) updateSlide(currentSlide + 1); };
  const prevSlide = () => { if (currentSlide > 0) updateSlide(currentSlide - 1); };

  const renderSlide = () => {
    const slide = slides[currentSlide];
    const P = THEME.primary;

    // Helper for chapter icons
    const ChapterIcon = ({ icon, size = 32 }) => {
      const props = { size, strokeWidth: 1.5 };
      if (icon === 'eye') return <Eye {...props} />;
      if (icon === 'heart') return <Heart {...props} />;
      if (icon === 'shield') return <Shield {...props} />;
      if (icon === 'users') return <Users {...props} />;
      if (icon === 'target') return <Target {...props} />;
      if (icon === 'brain') return <Brain {...props} />;
      return <BookOpen {...props} />;
    };

    if (slide.type === 'title') {
      return (
        <div style={{
          background: THEME.gradient,
          minHeight: '500px', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', padding: '3rem',
          color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden'
        }}>
          {/* Decorative floating shapes */}
          <div style={{ position: 'absolute', top: '-30px', left: '-30px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', top: '60px', right: '40px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'absolute', bottom: '40px', left: '60px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <div style={{ position: 'absolute', bottom: '-20px', right: '-20px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
          {/* Decorative dots pattern */}
          <div style={{ position: 'absolute', top: '20px', right: '80px', display: 'grid', gridTemplateColumns: 'repeat(5,8px)', gap: '8px', opacity: 0.15 }}>
            {Array.from({ length: 15 }).map((_, i) => <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'white' }} />)}
          </div>
          <div style={{ position: 'absolute', bottom: '30px', left: '80px', display: 'grid', gridTemplateColumns: 'repeat(4,8px)', gap: '8px', opacity: 0.12 }}>
            {Array.from({ length: 12 }).map((_, i) => <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'white' }} />)}
          </div>

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{
              width: '90px', height: '90px', borderRadius: '24px', background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '1.5rem', margin: '0 auto 1.5rem', border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <Baby size={44} />
            </div>
            <div style={{
              display: 'inline-block', padding: '0.3rem 1.2rem', borderRadius: '50px',
              background: 'rgba(255,255,255,0.15)', fontSize: '0.8rem', fontWeight: 600,
              letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '1rem',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              TP IEPE — Bloc 1
            </div>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, marginBottom: '1rem', lineHeight: 1.1 }}>{slide.title}</h1>
            <p style={{ fontSize: '1.35rem', maxWidth: '750px', opacity: 0.95, lineHeight: '1.6' }}>{slide.subtitle}</p>
            <div style={{
              marginTop: '2rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap'
            }}>
              {['4 chapitres', '41 slides', 'Audio inclus'].map((tag, i) => (
                <span key={i} style={{
                  padding: '0.35rem 0.85rem', borderRadius: '50px', background: 'rgba(255,255,255,0.12)',
                  fontSize: '0.78rem', fontWeight: 500, border: '1px solid rgba(255,255,255,0.15)'
                }}>{tag}</span>
              ))}
            </div>
            <p style={{ marginTop: '1.25rem', fontSize: '0.85rem', opacity: 0.6 }}>Titre Professionnel Intervenant Éducatif Petite Enfance</p>
          </div>
        </div>
      );
    }

    if (slide.type === 'sommaire') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'linear-gradient(180deg, #F8FAF9 0%, #ECFDF5 100%)' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: P, marginBottom: '0.5rem', textAlign: 'center' }}>{slide.title}</h2>
          <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.85rem', marginBottom: '1.5rem' }}>Clique sur un chapitre pour y accéder directement</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {slide.sections.map((s, i) => (
              <div
                key={i}
                onClick={() => { if (s.targetSlide !== undefined) updateSlide(s.targetSlide); }}
                style={{
                  background: 'white', borderRadius: '14px', border: `1px solid ${s.color}25`,
                  overflow: 'hidden', cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${s.color}20`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
                data-testid={`sommaire-section-${i}`}
              >
                <div style={{
                  background: `linear-gradient(135deg, ${s.color}, ${s.color}CC)`,
                  padding: '0.7rem 1rem', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                    {s.num !== 'Intro' ? `Chapitre ${s.num}` : 'Introduction'} — {s.title}
                  </span>
                  <Play size={14} fill="white" style={{ opacity: 0.7 }} />
                </div>
                <ul style={{ padding: '0.75rem 1rem', margin: 0, listStyle: 'none' }}>
                  {s.items.map((item, j) => (
                    <li key={j} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: '#475569', marginBottom: '0.3rem' }}>
                      <CheckCircle size={12} color={s.color} style={{ flexShrink: 0 }} /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'section') {
      const sColor = slide.chapterColor || P;
      const sGrad = `linear-gradient(135deg, ${sColor} 0%, ${sColor}CC 50%, ${sColor}99 100%)`;
      return (
        <div style={{
          background: sGrad, minHeight: '500px', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', padding: '3rem',
          color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden'
        }}>
          {/* Decorative background elements */}
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <div style={{ position: 'absolute', top: '50%', left: '10%', width: '60px', height: '60px', borderRadius: '12px', background: 'rgba(255,255,255,0.04)', transform: 'rotate(45deg)' }} />
          <div style={{ position: 'absolute', top: '30%', right: '15%', width: '40px', height: '40px', borderRadius: '8px', background: 'rgba(255,255,255,0.06)', transform: 'rotate(30deg)' }} />
          {/* Decorative line */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'rgba(255,255,255,0.2)' }} />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            {slide.chapterNum && (
              <div style={{
                width: '70px', height: '70px', borderRadius: '20px',
                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.25rem', border: '1px solid rgba(255,255,255,0.2)',
                fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-1px'
              }}>
                {slide.chapterNum}
              </div>
            )}
            {!slide.chapterNum && slide.chapterIcon && (
              <div style={{
                width: '70px', height: '70px', borderRadius: '20px',
                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.25rem', border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <ChapterIcon icon={slide.chapterIcon} />
              </div>
            )}
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '0.75rem', letterSpacing: '-0.5px' }}>{slide.title}</h2>
            {slide.subtitle && (
              <p style={{
                fontSize: '1.2rem', opacity: 0.92, maxWidth: '600px', lineHeight: 1.5,
                background: 'rgba(255,255,255,0.1)', padding: '0.75rem 1.5rem',
                borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)'
              }}>
                {slide.subtitle}
              </p>
            )}
          </div>
        </div>
      );
    }

    if (slide.type === 'content') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem' }}>{slide.title}</h2>
          {slide.points && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
              {slide.points.map((pt, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.5rem 0.75rem', background: i % 2 === 0 ? THEME.light : 'white', borderRadius: '8px', borderLeft: `3px solid ${P}` }}>
                  <CheckCircle size={15} color={P} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem', color: '#1E293B', lineHeight: 1.5 }}>{pt}</span>
                </div>
              ))}
            </div>
          )}
          {slide.pillars && (
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', justifyContent: 'center' }}>
              {slide.pillars.map((p, i) => (
                <div key={i} style={{ padding: '0.75rem 1.5rem', background: THEME.gradient, color: 'white', borderRadius: '50px', fontWeight: 600, fontSize: '0.9rem' }}>{p}</div>
              ))}
            </div>
          )}
          {slide.signes && (
            <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', borderLeft: '4px solid #F59E0B' }}>
              <strong style={{ color: '#92400E', display: 'block', marginBottom: '0.5rem' }}>Signes de fatigue :</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {slide.signes.map((s, i) => (
                  <span key={i} style={{ background: '#FDE68A', padding: '0.25rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem', color: '#78350F' }}>{s}</span>
                ))}
              </div>
            </div>
          )}
          {slide.dangers && (
            <div style={{ background: '#FEF2F2', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', borderLeft: '4px solid #EF4444' }}>
              <strong style={{ color: '#991B1B', display: 'block', marginBottom: '0.5rem' }}>Dangers relationnels :</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {slide.dangers.map((d, i) => (
                  <span key={i} style={{ background: '#FECACA', padding: '0.25rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem', color: '#7F1D1D' }}>{d}</span>
                ))}
              </div>
            </div>
          )}
          {slide.posture && (
            <div style={{ background: THEME.light, padding: '1rem', borderRadius: '10px', marginBottom: '1rem', borderLeft: `4px solid ${P}` }}>
              <strong style={{ color: THEME.accent }}>Posture professionnelle :</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
                {slide.posture.map((p, i) => <li key={i} style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.2rem' }}>{p}</li>)}
              </ul>
            </div>
          )}
          {slide.warning && (
            <div style={{ background: '#FEF3C7', padding: '0.75rem 1rem', borderRadius: '8px', borderLeft: '4px solid #F59E0B', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={16} color="#D97706" />
                <span style={{ fontWeight: 600, color: '#92400E', fontSize: '0.9rem' }}>{slide.warning}</span>
              </div>
            </div>
          )}
          {slide.highlight && (
            <div style={{ background: `${P}10`, padding: '0.75rem 1rem', borderRadius: '8px', borderLeft: `4px solid ${P}` }}>
              <span style={{ fontSize: '0.9rem', color: THEME.accent, fontStyle: 'italic' }}>{slide.highlight}</span>
            </div>
          )}
          {slide.quote && (
            <div style={{ background: `${P}08`, padding: '1rem', borderRadius: '10px', marginTop: '0.75rem', borderLeft: `4px solid ${P}` }}>
              <p style={{ fontStyle: 'italic', color: THEME.accent, fontSize: '0.95rem', lineHeight: 1.5 }}>{slide.quote}</p>
            </div>
          )}
        </div>
      );
    }

    if (slide.type === 'method') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: P, marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', background: 'white', borderRadius: '12px', border: `1px solid ${P}25`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: THEME.gradient, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0, fontSize: '1.1rem' }}>
                  {i + 1}
                </div>
                <div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: P, marginBottom: '0.25rem' }}>{step.label}</h3>
                  <p style={{ fontSize: '0.9rem', color: '#64748B', lineHeight: 1.4 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          {slide.quote && (
            <div style={{ background: `${P}08`, padding: '1rem', borderRadius: '10px', marginTop: '1.25rem', maxWidth: '700px', margin: '1.25rem auto 0', borderLeft: `4px solid ${P}` }}>
              <p style={{ fontStyle: 'italic', color: THEME.accent, fontSize: '0.95rem', textAlign: 'center' }}>{slide.quote}</p>
            </div>
          )}
        </div>
      );
    }

    if (slide.type === 'grid') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
            {slide.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: i < 9 ? THEME.light : '#FFF7ED', borderRadius: '10px', border: `1px solid ${i < 9 ? P : '#F97316'}25` }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: i < 9 ? P : '#F97316', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold', flexShrink: 0 }}>
                  {item.num}
                </div>
                <span style={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500 }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'comparison') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem' }}>{slide.title}</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '10px', overflow: 'hidden' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', background: '#FEE2E2', color: '#991B1B', textAlign: 'left', fontWeight: 600 }}>Interprétation</th>
                  <th style={{ padding: '0.75rem 1rem', background: THEME.light, color: THEME.accent, textAlign: 'left', fontWeight: 600 }}>Observation professionnelle</th>
                </tr>
              </thead>
              <tbody>
                {slide.wrong.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#FAFAFA' : 'white' }}>
                    <td style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #E5E7EB' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: '#EF4444' }}>&#10007;</span>
                        <span style={{ color: '#991B1B', fontSize: '0.9rem' }}>{row.label}</span>
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #E5E7EB' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <CheckCircle size={14} color={P} />
                        <span style={{ color: '#1E293B', fontSize: '0.9rem' }}>{row.correct}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {slide.highlight && (
            <div style={{ background: `${P}10`, padding: '0.75rem 1rem', borderRadius: '8px', borderLeft: `4px solid ${P}`, marginTop: '1rem' }}>
              <span style={{ fontSize: '0.9rem', color: THEME.accent, fontWeight: 500 }}>{slide.highlight}</span>
            </div>
          )}
        </div>
      );
    }

    if (slide.type === 'table') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem' }}>{slide.title}</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '10px', overflow: 'hidden' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', background: P, color: 'white', textAlign: 'left' }}>Situation observée</th>
                  <th style={{ padding: '0.75rem 1rem', background: P, color: 'white', textAlign: 'left' }}>Besoin possible</th>
                </tr>
              </thead>
              <tbody>
                {slide.rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                    <td style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #E5E7EB', fontSize: '0.9rem' }}>{row.situation}</td>
                    <td style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #E5E7EB', fontSize: '0.9rem', fontWeight: 500, color: THEME.accent }}>{row.besoin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (slide.type === 'example') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1rem' }}>{slide.title}</h2>
          <div style={{ background: '#FFF7ED', padding: '0.75rem 1rem', borderRadius: '8px', borderLeft: '4px solid #F97316', marginBottom: '1rem' }}>
            <strong style={{ color: '#9A3412' }}>Situation :</strong> <span style={{ color: '#1E293B' }}>{slide.situation}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ background: '#EFF6FF', padding: '0.75rem', borderRadius: '8px', borderLeft: '4px solid #3B82F6' }}>
              <strong style={{ color: '#1E40AF', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Observation</strong>
              {slide.observation.map((o, i) => <div key={i} style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.2rem' }}>- {o}</div>)}
            </div>
            <div style={{ background: '#FFF7ED', padding: '0.75rem', borderRadius: '8px', borderLeft: '4px solid #F97316' }}>
              <strong style={{ color: '#9A3412', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Analyse</strong>
              {slide.analyse.map((a, i) => <div key={i} style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.2rem' }}>- {a}</div>)}
            </div>
            <div style={{ background: THEME.light, padding: '0.75rem', borderRadius: '8px', borderLeft: `4px solid ${P}` }}>
              <strong style={{ color: THEME.accent, fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Action</strong>
              {slide.action.map((a, i) => <div key={i} style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.2rem' }}>- {a}</div>)}
            </div>
          </div>
          {slide.evaluation && (
            <div style={{ background: '#F0FDF4', padding: '0.75rem 1rem', borderRadius: '8px', borderLeft: '4px solid #22C55E', marginBottom: '0.75rem' }}>
              <strong style={{ color: '#166534', fontSize: '0.85rem' }}>Évaluation :</strong> <span style={{ fontSize: '0.85rem', color: '#374151' }}>{slide.evaluation}</span>
            </div>
          )}
          {slide.formulation && (
            <div style={{ background: `${P}08`, padding: '0.75rem 1rem', borderRadius: '8px', borderLeft: `4px solid ${P}` }}>
              <p style={{ fontStyle: 'italic', color: THEME.accent, fontSize: '0.9rem', lineHeight: 1.5 }}>{slide.formulation}</p>
            </div>
          )}
        </div>
      );
    }

    if (slide.type === 'posture') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: P, marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ background: '#FEF2F2', borderRadius: '12px', padding: '1.25rem', border: '1px solid #FECACA' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <AlertTriangle size={20} color="#EF4444" />
                <strong style={{ color: '#991B1B' }}>A éviter</strong>
              </div>
              {slide.wrong.map((w, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#EF4444', fontWeight: 'bold' }}>&#10007;</span>
                  <span style={{ fontSize: '0.9rem', color: '#7F1D1D' }}>{w}</span>
                </div>
              ))}
            </div>
            <div style={{ background: THEME.light, borderRadius: '12px', padding: '1.25rem', border: `1px solid ${P}30` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <CheckCircle size={20} color={P} />
                <strong style={{ color: THEME.accent }}>Bonne pratique</strong>
              </div>
              {slide.right.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <CheckCircle size={14} color={P} />
                  <span style={{ fontSize: '0.9rem', color: '#065F46' }}>{r}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (slide.type === 'multi') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
            {slide.sections.map((s, i) => (
              <div key={i} style={{ background: 'white', borderRadius: '10px', border: `1px solid ${P}25`, overflow: 'hidden' }}>
                <div style={{ background: `${P}10`, padding: '0.6rem 0.75rem', borderBottom: `2px solid ${P}` }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: THEME.accent }}>{s.title}</h3>
                </div>
                <div style={{ padding: '0.6rem 0.75rem' }}>
                  {s.items.map((item, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.3rem' }}>
                      <CheckCircle size={12} color={P} style={{ marginTop: '3px', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.82rem', color: '#374151' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'jurybox') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: P, marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ maxWidth: '650px', margin: '0 auto', background: THEME.light, borderRadius: '16px', padding: '1.5rem', border: `2px solid ${P}30` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
              <Target size={22} color={P} />
              <strong style={{ color: THEME.accent, fontSize: '1rem' }}>Le jury attend que :</strong>
            </div>
            {slide.points.map((pt, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.65rem', padding: '0.5rem 0.75rem', background: 'white', borderRadius: '8px' }}>
                <CheckCircle size={16} color={P} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: '#1E293B' }}>{pt}</span>
              </div>
            ))}
          </div>
          {slide.quote && (
            <div style={{ background: `${P}08`, padding: '1rem', borderRadius: '10px', marginTop: '1.25rem', maxWidth: '650px', margin: '1.25rem auto 0', borderLeft: `4px solid ${P}` }}>
              <p style={{ fontStyle: 'italic', color: THEME.accent, fontSize: '0.95rem', textAlign: 'center', lineHeight: 1.5 }}>{slide.quote}</p>
            </div>
          )}
        </div>
      );
    }

    if (slide.type === 'congratulations') {
      return (
        <div style={{ background: THEME.gradient, minHeight: '500px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'white', textAlign: 'center' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', fontSize: '2.5rem' }}>
            &#127891;
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>{slide.title}</h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.95, marginBottom: '1.5rem' }}>{slide.message}</p>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '1.25rem', maxWidth: '500px' }}>
            {slide.details.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <CheckCircle size={16} />
                <span style={{ fontSize: '0.9rem' }}>{d}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  // Chapter markers for mini-progress
  const CHAPTERS = [
    { label: 'Intro', slide: 2, color: THEME.primary },
    { label: 'Ch.1', slide: 10, color: '#E75480' },
    { label: 'Ch.2', slide: 17, color: '#3B82F6' },
    { label: 'Ch.3', slide: 25, color: '#F97316' },
    { label: 'Ch.4', slide: 33, color: '#8B5CF6' },
    { label: 'Fin', slide: 38, color: THEME.primary }
  ];

  return (
    <div style={{ background: '#f8fafc', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
      {/* Resume banner */}
      {showResume && savedSlide > 0 && currentSlide === 0 && (
        <div data-testid="resume-banner" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.65rem 1.5rem', background: 'linear-gradient(90deg, #059669, #10B981)',
          color: 'white', fontSize: '0.85rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <RotateCcw size={16} />
            <span>Tu t'es arrêté(e) à la slide {savedSlide + 1}/{slides.length}</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              data-testid="resume-btn"
              onClick={() => { updateSlide(savedSlide); setShowResume(false); }}
              style={{
                padding: '0.35rem 1rem', borderRadius: '50px', background: 'white',
                color: THEME.primary, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem'
              }}
            >
              Reprendre
            </button>
            <button
              onClick={() => setShowResume(false)}
              style={{
                padding: '0.35rem 0.75rem', borderRadius: '50px', background: 'rgba(255,255,255,0.2)',
                color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.8rem'
              }}
            >
              Recommencer
            </button>
          </div>
        </div>
      )}

      {/* Main progress bar */}
      <div style={{ height: '4px', background: '#e2e8f0' }}>
        <div style={{ height: '100%', background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.secondary})`, width: `${((currentSlide + 1) / slides.length) * 100}%`, transition: 'width 0.3s ease' }} />
      </div>

      {/* Chapter progress dots */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
        padding: '0.5rem 1rem', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0'
      }}>
        {CHAPTERS.map((ch, i) => {
          const isActive = currentSlide >= ch.slide;
          const isCurrent = i < CHAPTERS.length - 1
            ? currentSlide >= ch.slide && currentSlide < CHAPTERS[i + 1].slide
            : currentSlide >= ch.slide;
          return (
            <React.Fragment key={i}>
              <button
                onClick={() => updateSlide(ch.slide)}
                data-testid={`chapter-dot-${i}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.2rem 0.55rem', borderRadius: '50px', border: 'none',
                  background: isCurrent ? ch.color : isActive ? `${ch.color}20` : '#e2e8f0',
                  color: isCurrent ? 'white' : isActive ? ch.color : '#94a3b8',
                  cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                  transition: 'all 0.2s', whiteSpace: 'nowrap'
                }}
              >
                {isActive && <CheckCircle size={10} />}
                {ch.label}
              </button>
              {i < CHAPTERS.length - 1 && (
                <div style={{
                  width: '16px', height: '2px',
                  background: isActive ? ch.color : '#e2e8f0',
                  borderRadius: '1px', transition: 'background 0.3s'
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {renderSlide()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 2rem', background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem',
          background: currentSlide === 0 ? '#e2e8f0' : THEME.primary,
          color: currentSlide === 0 ? '#94a3b8' : 'white',
          border: 'none', borderRadius: '0.5rem', cursor: currentSlide === 0 ? 'not-allowed' : 'pointer', fontWeight: '500'
        }}>
          <ChevronLeft size={20} /> Précédent
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={handleTextToSpeech} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem',
            background: isPlaying ? THEME.primary : '#f8fafc',
            color: isPlaying ? 'white' : THEME.primary,
            border: `2px solid ${THEME.primary}`, borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '500'
          }}>
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : isPlaying ? <VolumeX size={20} /> : <Volume2 size={20} />}
            {isLoading ? 'Chargement...' : isPlaying ? 'Arrêter' : 'Écouter'}
          </button>
          <span style={{ padding: '0.5rem 1rem', background: THEME.primary, color: 'white', borderRadius: '1rem', fontSize: '0.875rem', fontWeight: '500' }}>
            {currentSlide + 1} / {slides.length}
          </span>
        </div>

        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem',
          background: currentSlide === slides.length - 1 ? '#e2e8f0' : THEME.primary,
          color: currentSlide === slides.length - 1 ? '#94a3b8' : 'white',
          border: 'none', borderRadius: '0.5rem', cursor: currentSlide === slides.length - 1 ? 'not-allowed' : 'pointer', fontWeight: '500'
        }}>
          Suivant <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default TPIEPEBloc1Presentation;
